/**
 * Typed API client.
 *
 * Two things it does that a bare `fetch` wrapper does not:
 *  - transparently refreshes an expired access token **once** per request and
 *    replays the original call, with a single shared in-flight refresh so a
 *    burst of parallel 401s does not fire N refreshes;
 *  - unwraps the backend's uniform `{error:{code,message,details}}` envelope
 *    into a typed `ApiError`, so every screen can render `err.message` and
 *    switch on `err.code` without reparsing JSON.
 */

const BASE = '/api/v1'

const ACCESS_KEY = 'qr24-access'
const REFRESH_KEY = 'qr24-refresh'

export interface ApiErrorDetails {
  field?: string
  message?: string
  hint?: string
  [key: string]: unknown
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly details?: ApiErrorDetails,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Field name to highlight in a form, when the backend told us one. */
  get field(): string | undefined {
    return this.details?.field as string | undefined
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(ACCESS_KEY)
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY)
  },
  set(access: string, refresh: string) {
    localStorage.setItem(ACCESS_KEY, access)
    localStorage.setItem(REFRESH_KEY, refresh)
  },
  clear() {
    localStorage.removeItem(ACCESS_KEY)
    localStorage.removeItem(REFRESH_KEY)
  },
}

type Listener = () => void
const logoutListeners = new Set<Listener>()

/** Subscribe to "the refresh token is dead, drop the session". Returns an unsubscribe. */
export function onForcedLogout(fn: Listener): () => void {
  logoutListeners.add(fn)
  return () => {
    logoutListeners.delete(fn)
  }
}

function forceLogout() {
  tokens.clear()
  logoutListeners.forEach((fn) => fn())
}

let refreshInFlight: Promise<boolean> | null = null

async function refreshTokens(): Promise<boolean> {
  const refresh = tokens.refresh
  if (!refresh) return false

  // Collapse concurrent refreshes into one network call.
  refreshInFlight ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
      })
      if (!res.ok) return false
      const data = await res.json()
      tokens.set(data.accessToken, data.refreshToken)
      return true
    } catch {
      return false
    } finally {
      // Release on the next tick so all awaiting callers see the same result.
      queueMicrotask(() => {
        refreshInFlight = null
      })
    }
  })()

  return refreshInFlight
}

export interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  auth?: boolean
  /** Return the raw Response instead of parsed JSON (binary downloads). */
  raw?: boolean
  idempotencyKey?: string
}

async function toApiError(res: Response): Promise<ApiError> {
  let code = 'http_error'
  let message = `HTTP ${res.status}`
  let details: ApiErrorDetails | undefined

  try {
    const payload = await res.json()
    if (payload?.error) {
      code = payload.error.code ?? code
      message = payload.error.message ?? message
      details = payload.error.details
    }
  } catch {
    /* non-JSON error body — keep the generic message */
  }
  return new ApiError(code, message, res.status, details)
}

export async function request<T = unknown>(
  path: string,
  { body, auth = true, raw = false, idempotencyKey, ...init }: RequestOptions = {},
): Promise<T> {
  const send = async (): Promise<Response> => {
    const headers = new Headers(init.headers)
    if (body !== undefined && !(body instanceof FormData)) {
      headers.set('Content-Type', 'application/json')
    }
    if (auth && tokens.access) {
      headers.set('Authorization', `Bearer ${tokens.access}`)
    }
    if (idempotencyKey) headers.set('Idempotency-Key', idempotencyKey)

    return fetch(`${BASE}${path}`, {
      ...init,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  }

  let res = await send()

  if (res.status === 401 && auth && tokens.refresh) {
    if (await refreshTokens()) {
      res = await send()
    } else {
      forceLogout()
      throw new ApiError('unauthorized', 'Сесія завершилася — увійдіть знову', 401)
    }
  }

  if (!res.ok) throw await toApiError(res)
  if (raw) return res as unknown as T
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'GET' }),
  post: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, opts?: RequestOptions) =>
    request<T>(path, { ...opts, method: 'PATCH', body }),
  del: <T>(path: string, opts?: RequestOptions) => request<T>(path, { ...opts, method: 'DELETE' }),
}

/** Generates the `Idempotency-Key` value the docs tell integrators to send. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}
